import { getSolarRecommand } from '@/service/solar-calculator';

export interface ProductAPIType {
    productId: string;
    variantId: string;
    img: string;
    variantImg: string;
    productTitle: string;
    skuTitle: string;
    sku: string;
    handle: string;
    price: number;
    compareAtPrice: number;
    inventoryQuantity: number;
    inventoryPolicy: string;
}

export interface ProductItemType extends ProductAPIType {
    link_more: string;
    link_buy: string;
    quantity: number;
    id: string;
    power: string;
    descArr: string[];
    icons: string[];
    storage: number;
    children: SolarItemType[];
    childrenIds: string[];
}

export interface SolarItemType extends ProductItemType {
    id: string;
    energy: number;
    quantity: number;
    recommand: number[];
    isActive: boolean;
    parentVariantId: string;
}

export interface DataLocalType {
    solar_panels: SolarItemType[];
    products: ProductItemType[];
}

export interface RecommandType {
    [key: string]: {
        [key: string]: number[];
    };
}

// 太阳能板 Solar Panel + Camping Light, TRAIL DC Bag等搭配产品
const SP_45W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 45,
    id: '45w',
};

const SP_60W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 60,
    id: '60w',
};

const SP_110W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 110,
    id: '110w',
};

const SP_160W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 160,
    id: '160w',
};

const SP_160W_BIFACIAL: SolarItemType = {
    ...({} as SolarItemType),
    energy: 160,
    id: '160w_bifacial',
};

const SP_220W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 220,
    id: '220w',
};

const SP_220W_BIFACIAL: SolarItemType = {
    ...({} as SolarItemType),
    energy: 220,
    id: '220w_bifacial',
};

const SP_400W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 400,
    id: '400w',
};

const CAMPING_LIGHT: SolarItemType = {
    ...({} as SolarItemType),
    energy: 0,
    id: 'campingLight',
};

const TRAIL_DC_BAG: SolarItemType = {
    ...({} as SolarItemType),
    energy: 0,
    id: 'trailDCBag',
};

// 产品数据
const TRAIL_200: ProductItemType = {
    ...({} as ProductItemType),
    id: 'trail200',
    storage: 0.19,
};

const TRAIL_300: ProductItemType = {
    ...({} as ProductItemType),
    id: 'trail300',
    storage: 0.28,
};

const RIVER_3: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river3',
    storage: 0.25,
};

const RIVER_3_PLUS: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river3Plus',
    storage: 0.28,
};

const RIVER_3_MAX: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river3Max',
    storage: 0.57,
};

const RIVER_2_PRO: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river2Pro',
    storage: 0.77,
};

const RIVER_PRO: ProductItemType = {
    ...({} as ProductItemType),
    id: 'riverPro',
    storage: 0.72,
};

const RIVER_2_PRO_700: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river2Pro700',
    storage: 0.72,
};

const RIVER_2_MAX: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river2Max',
    storage: 0.51,
};

const RIVER_2: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river2',
    storage: 0.27,
};

const RIVER_2_230: ProductItemType = {
    ...({} as ProductItemType),
    id: 'river2230',
    storage: 0.23,
};

const DELTA_2: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta2',
    storage: 1.04,
};

const DELTA_2_MAX: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta2Max',
    storage: 2.05,
};

// 推荐搭配数量
export const RecommandMap: RecommandType = {
    trail200: {
        campingLight: [0],
        trailDCBag: [0],
    },
    trail300: {
        '45w': [1],
        '60w': [1],
    },
    river3: {
        '45w': [1],
        '60w': [1, 2],
        '110w': [1],
    },
    river3Plus: {
        '45w': [1, 2, 3, 4, 5, 6],
        '60w': [1],
        '110w': [1, 2],
        '160w': [1],
        '220w': [1],
    },
    river3Max: {
        '45w': [1, 2, 3, 4, 5],
        '60w': [1, 2],
        '110w': [1, 2],
        '160w': [1],
        '220w': [1],
    },
    river3Pro: {
        '45w': [1, 2, 3, 4, 5, 6],
        '60w': [1, 2],
        '110w': [1, 2],
        '160w': [1],
        '220w': [1],
    },
    river2Pro: {
        '45w': [1, 2, 3, 4, 5],
        '60w': [1, 2],
        '110w': [1, 2],
        '160w': [1],
        '220w': [1],
        '220w_bifacial': [1],
    },
    river2Pro700: {
        '160w': [1],
        '220w': [1],
    },
    river2Max: {
        '60w': [1, 2],
        '110w': [1, 2],
        '160w': [1],
        '160w_bifacial': [1],
    },
    river2: {
        '45w': [1],
        '60w': [1],
        '110w': [1],
    },
    river2230: {
        '45w': [1],
        '60w': [1],
        '110w': [1],
    },
    riverPro: {
        '60w': [1, 2],
        '110w': [1, 2],
        '160w': [1],
        '160w_bifacial': [1],
    },
    delta2: {
        '110w': [1, 2, 4],
        '160w': [1, 2],
        '220w': [1, 2],
        '400w': [1],
    },
    delta2Max: {
        '160w': [1, 2, 3, 4],
        '220w': [1, 2, 3, 4],
        '400w': [1, 2],
    },
};

// 不同国家数据
// 北美
const dataUS: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '7264162676809',
            variantId: '41731341353033',
        },
        {
            ...SP_60W,
            productId: '4478011113545',
            variantId: '39998108401737',
        },
        {
            ...SP_110W,
            productId: '4318647222345',
            variantId: '39998109941833',
        },
        {
            ...SP_160W,
            productId: '4605523198025',
            variantId: '40573832429641',
        },
        {
            ...SP_220W,
            productId: '6687135957065',
            variantId: '40573832495177',
        },
        {
            ...CAMPING_LIGHT,
            productId: '6777224101961',
            variantId: '40023315054665',
        },
        {
            ...TRAIL_DC_BAG,
            productId: '15376019161161',
            variantId: '54647146381385',
        },
    ],
    products: [
        {
            ...TRAIL_200,
            productId: '15350808346697',
            variantId: '54553979682889',
            link_more: 'https://us.ecoflow.com/products/trail-series?variant=54553979682889',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'Drone'],
            childrenIds: ['campingLight', 'trailDCBag'],
        },
        {
            ...TRAIL_300,
            productId: '15350808346697',
            variantId: '54553980043337',
            link_more: 'https://us.ecoflow.com/products/trail-series?variant=54553980043337',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'Drone'],
            childrenIds: ['45w', '60w'],
        },
        {
            ...RIVER_3,
            productId: '7290965622857',
            variantId: '41636504305737',
            link_more: 'https://us.ecoflow.com/products/river-3-portable-power-station?variant=41636504305737',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w'],
        },
        {
            ...RIVER_3_PLUS,
            productId: '7290968473673',
            variantId: '41636514136137',
            link_more: 'https://us.ecoflow.com/products/river-3-plus-portable-power-station?variant=41636514136137',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '110w', '160w', '220w'],
        },
        {
            ...RIVER_3_MAX,
            productId: '7290968473673',
            variantId: '41636515807305',
            link_more: 'https://us.ecoflow.com/products/river-3-plus-portable-power-station?variant=41636515807305',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '110w', '160w', '220w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '6859087282249',
            variantId: '54320636198985',
            link_more: 'https://us.ecoflow.com/products/river-2-pro-portable-power-station?variant=54320636198985',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '110w', '160w', '220w'],
        },
    ],
};

const dataCA: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '8292312449178',
            variantId: '52438137438579',
        },
        {
            ...SP_60W,
            productId: '7539941834906',
            variantId: '43797586608282',
        },
        {
            ...SP_110W,
            productId: '5511642644634',
            variantId: '43725961199770',
        },
        {
            ...SP_160W,
            productId: '6609119936666',
            variantId: '43762372739226',
        },
        {
            ...SP_220W,
            productId: '7539942064282',
            variantId: '43725934461082',
        },
        {
            ...CAMPING_LIGHT,
            productId: '7590554828954',
            variantId: '42864166273178',
        },
        {
            ...TRAIL_DC_BAG,
            productId: '15142271648115',
            variantId: '52813020266867',
        },
    ],
    products: [
        {
            ...TRAIL_200,
            productId: '15127284351347',
            variantId: '52734893490547',
            link_more: 'https://ca.ecoflow.com/products/trail-series?variant=52734893490547',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'Drone'],
            childrenIds: ['campingLight', 'trailDCBag'],
        },
        {
            ...TRAIL_300,
            productId: '15127284351347',
            variantId: '52734893523315',
            link_more: 'https://ca.ecoflow.com/products/trail-series?variant=52734893523315',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'Drone'],
            childrenIds: ['45w', '60w'],
        },
        {
            ...RIVER_3,
            productId: '8335910928538',
            variantId: '45166394409114',
            link_more: 'https://ca.ecoflow.com/products/river-3-portable-power-station?variant=45166394409114',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w'],
        },
        {
            ...RIVER_3_PLUS,
            productId: '15039583682931',
            variantId: '52355185279347',
            link_more: 'https://ca.ecoflow.com/products/river-3-plus-portable-power-station?variant=52355185279347',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '110w', '160w', '220w'],
        },
        {
            ...RIVER_3_MAX,
            productId: '15039583682931',
            variantId: '52355124134259',
            link_more: 'https://ca.ecoflow.com/products/river-3-plus-portable-power-station?variant=52355124134259',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '110w', '160w', '220w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7539942064282',
            variantId: '52471099490675',
            link_more: 'https://ca.ecoflow.com/products/river-2-pro-portable-power-station?variant=52471099490675',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['45w', '110w', '160w', '220w'],
        },
    ],
};

// 欧洲
const dataEU: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '9647636709719',
            variantId: '49335090348375',
        },
        {
            ...SP_60W,
            productId: '8453648777559',
            variantId: '49800165556567',
        },
        {
            ...SP_110W,
            productId: '10431164711255',
            variantId: '52266397565271',
        },
        {
            ...SP_160W,
            productId: '10106925056343',
            variantId: '50969351487831',
        },
        {
            ...SP_220W,
            productId: '10107077493079',
            variantId: '50969865519447',
        },
    ],
    products: [
        {
            ...RIVER_3_PLUS,
            productId: '9912452940119',
            variantId: '50144528204119',
            link_more: 'https://eu.ecoflow.com/products/river-3-plus-portable-power-station?variant=50144528204119',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w', '160w'],
        },
        {
            ...RIVER_3,
            productId: '9712614179159',
            variantId: '49500668789079',
            link_more: 'https://eu.ecoflow.com/products/river-3-portable-power-station?variant=49500668789079',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '110w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7472877699236',
            variantId: '46822242713943',
            link_more: 'https://eu.ecoflow.com/products/river-2-pro-portable-power-station?variant=46822242713943',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '220w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7472877338788',
            variantId: '46822249365847',
            link_more: 'https://eu.ecoflow.com/products/river-2-max-portable-power-station?variant=46822249365847',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '160w'],
        },
    ],
};

const dataUK: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '9563272249683',
            variantId: '49662916264275',
        },
        {
            ...SP_60W,
            productId: '8434339938643',
            variantId: '50102276489555',
        },
        {
            ...SP_110W,
            productId: '10305317667155',
            variantId: '52554238132563',
        },
        {
            ...SP_160W,
            productId: '9947421114707',
            variantId: '52277093892435',
        },
        {
            ...SP_220W,
            productId: '9966309441875',
            variantId: '51216552886611',
        },
    ],
    products: [
        {
            ...RIVER_3_PLUS,
            productId: '9803871682899',
            variantId: '50552824856915',
            link_more: 'https://uk.ecoflow.com/products/river-3-plus-portable-power-station?variant=50552824856915',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w', '160w'],
        },
        {
            ...RIVER_3,
            productId: '9606214418771',
            variantId: '49797546901843',
            link_more: 'https://uk.ecoflow.com/products/river-3-portable-power-station?variant=49797546901843',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '110w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7388078407872',
            variantId: '46728982724947',
            link_more: 'https://uk.ecoflow.com/products/river-2-pro-portable-power-station?variant=46728982724947',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '220w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7388078670016',
            variantId: '46729106719059',
            link_more: 'https://uk.ecoflow.com/products/river-2-max-portable-power-station?variant=46729106719059',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '160w'],
        },
    ],
};

const dataDE: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '9658293584207',
            variantId: '49933387268431',
        },
        {
            ...SP_60W,
            productId: '8551057686863',
            variantId: '55091917357437',
        },
        {
            ...SP_110W,
            productId: '15711105941885',
            variantId: '57526848946557',
        },
        {
            ...SP_160W,
            productId: '15321906676093',
            variantId: '56293334679933',
        },
        {
            ...SP_220W,
            productId: '15321895600509',
            variantId: '56293274190205',
        },
    ],
    products: [
        {
            ...RIVER_3_PLUS,
            productId: '15064412520829',
            variantId: '55476457177469',
            link_more: 'https://de.ecoflow.com/products/river-3-plus-portable-power-station?variant=55476457177469',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w', '160w'],
        },
        {
            ...RIVER_3,
            productId: '14827510268239',
            variantId: '53660091515215',
            link_more: 'https://de.ecoflow.com/products/river-3-portable-power-station?variant=53660091515215',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '110w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7815351075063',
            variantId: '46769125687631',
            link_more: 'https://de.ecoflow.com/products/river-2-pro-portable-power-station?variant=46769125687631',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '220w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7815349371127',
            variantId: '46769179066703',
            link_more: 'https://de.ecoflow.com/products/river-2-max-portable-power-station?variant=46769179066703',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '160w'],
        },
    ],
};

const dataFR: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '9533681008987',
            variantId: '49300034781531',
        },
        {
            ...SP_60W,
            productId: '8468805484891',
            variantId: '49861432410459',
        },
        {
            ...SP_110W,
            productId: '10242120745307',
            variantId: '51865825870171',
        },
        {
            ...SP_160W,
            productId: '9966244135259',
            variantId: '50834554093915',
        },
        {
            ...SP_220W,
            productId: '9976213274971',
            variantId: '50883301867867',
        },
    ],
    products: [
        {
            ...RIVER_3_PLUS,
            productId: '9781516861787',
            variantId: '50209756479835',
            link_more: 'https://fr.ecoflow.com/products/river-3-plus-portable-power-station?variant=50209756479835',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w', '160w'],
        },
        {
            ...RIVER_3,
            productId: '9582183481691',
            variantId: '49471449530715',
            link_more: 'https://fr.ecoflow.com/products/river-3-portable-power-station?variant=49471449530715',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '110w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7929781977314',
            variantId: '46833280024923',
            link_more: 'https://fr.ecoflow.com/products/river-2-pro-portable-power-station?variant=46833280024923',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '220w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7928802607330',
            variantId: '46833261445467',
            link_more: 'https://fr.ecoflow.com/products/river-2-max-portable-power-station?variant=46833261445467',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '160w'],
        },
    ],
};

const dataIT: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '9974190375261',
            variantId: '54275991634269',
        },
        {
            ...SP_60W,
            productId: '8438189392221',
            variantId: '54884655825272',
        },
        {
            ...SP_110W,
            productId: '14989722648952',
            variantId: '55377721327992',
        },
        {
            ...SP_160W,
            productId: '14940805202296',
            variantId: '55188402078072',
        },
        {
            ...SP_220W,
            productId: '14940806021496',
            variantId: '55188408238456',
        },
    ],
    products: [
        {
            ...RIVER_3_PLUS,
            productId: '14893036011896',
            variantId: '54969500795256',
            link_more: 'https://it.ecoflow.com/products/river-3-plus-portable-power-station?variant=54969500795256',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w', '160w'],
        },
        {
            ...RIVER_3,
            productId: '14734774665565',
            variantId: '54724579983709',
            link_more: 'https://it.ecoflow.com/products/river-3-portable-power-station?variant=54724579983709',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '110w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7842358984945',
            variantId: '47043066823005',
            link_more: 'https://it.ecoflow.com/products/river-2-pro-portable-power-station?variant=47043066823005',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '220w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7841513242865',
            variantId: '47043065250141',
            link_more: 'https://it.ecoflow.com/products/river-2-max-portable-power-station?variant=47043065250141',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '160w'],
        },
    ],
};

const dataES: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '9533573103963',
            variantId: '49300046479707',
        },
        {
            ...SP_60W,
            productId: '8465628135771',
            variantId: '49861559189851',
        },
        {
            ...SP_110W,
            productId: '10234409877851',
            variantId: '51834443268443',
        },
        {
            ...SP_160W,
            productId: '9976249975131',
            variantId: '50883397845339',
        },
        {
            ...SP_220W,
            productId: '9976244633947',
            variantId: '50883390636379',
        },
    ],
    products: [
        {
            ...RIVER_3_PLUS,
            productId: '9781516763483',
            variantId: '50209756053851',
            link_more: 'https://es.ecoflow.com/products/river-3-plus-portable-power-station?variant=50209756053851',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w', '160w'],
        },
        {
            ...RIVER_3,
            productId: '9582187151707',
            variantId: '49471467520347',
            link_more: 'https://es.ecoflow.com/products/river-3-portable-power-station?variant=49471467520347',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '110w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7850943152350',
            variantId: '46831957934427',
            link_more: 'https://es.ecoflow.com/products/river-2-pro-portable-power-station?variant=46831957934427',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '220w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7850071392478',
            variantId: '46831955542363',
            link_more: 'https://es.ecoflow.com/products/river-2-max-portable-power-station?variant=46831955542363',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '160w'],
        },
    ],
};

const dataNL: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '9935710191892',
            variantId: '51194413252884',
        },
        {
            ...SP_60W,
            productId: '9935705309460',
            variantId: '51194396934420',
        },
        {
            ...SP_110W,
            productId: '9935696822548',
            variantId: '51194369933588',
        },
        {
            ...SP_160W,
            productId: '9935714353428',
            variantId: '51427564355860',
        },
        {
            ...SP_220W,
            productId: '9935714386196',
            variantId: '51427570286868',
        },
    ],
    products: [
        {
            ...RIVER_3_PLUS,
            productId: '9935711568148',
            variantId: '51194421510420',
            link_more: 'https://nl.ecoflow.com/products/river-3-plus-portable-power-station?variant=51194421510420',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w', '160w'],
        },
        {
            ...RIVER_3,
            productId: '9935710912788',
            variantId: '51194414956820',
            link_more: 'https://nl.ecoflow.com/products/river-3-portable-power-station?variant=51194414956820',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WiFiRouter', 'Laptop', 'Display', 'MiniFridge'],
            childrenIds: ['45w', '110w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '9935700295956',
            variantId: '51194379239700',
            link_more: 'https://nl.ecoflow.com/products/river-2-pro-portable-power-station?variant=51194379239700',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '220w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '9935700132116',
            variantId: '51194378682644',
            link_more: 'https://nl.ecoflow.com/products/river-2-max-portable-power-station?variant=51194378682644',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'WaterPump', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['110w', '160w'],
        },
    ],
};

// ROW
const dataAU: DataLocalType = {
    solar_panels: [
        {
            ...SP_60W,
            productId: '8331077124294',
            variantId: '44112672030918',
        },
        {
            ...SP_110W,
            productId: '6734298185926',
            variantId: '42994541494470',
        },
        {
            ...SP_160W,
            productId: '8228927897798',
            variantId: '44409522553030',
        },
        {
            ...SP_220W,
            productId: '8258480472262',
            variantId: '43982554431686',
        },
    ],
    products: [
        {
            ...RIVER_2,
            productId: '7756518654150',
            variantId: '42649688244422',
            link_more: 'https://au.ecoflow.com/products/river-2-portable-power-station',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['60w', '110w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7756526026950',
            variantId: '42649690210502',
            link_more: 'https://au.ecoflow.com/products/river-2-max-portable-power-station',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['110w', '160w'],
        },
        {
            ...RIVER_2_PRO_700,
            productId: '8228927897798',
            variantId: '43848994357446',
            link_more: 'https://au.ecoflow.com/products/river-2-pro-700-portable-power-station',
            descArr: ['', ''],
            icons: ['Light', 'Phone', 'WaterPump'],
            childrenIds: ['160w', '220w'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7756526584006',
            variantId: '42673598824646',
            link_more: 'https://au.ecoflow.com/products/river-2-pro-portable-power-station',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Projector', 'MiniFridge'],
            childrenIds: ['160w', '220w'],
        },
    ],
};

const dataZA: DataLocalType = {
    solar_panels: [
        {
            ...SP_45W,
            productId: '7479098605777',
            variantId: '42492953886929',
        },
        {
            ...SP_60W,
            productId: '7417644482769',
            variantId: '42655292489937',
        },
        {
            ...SP_110W,
            productId: '7564101976273',
            variantId: '42489077334225',
        },
        {
            ...SP_160W, // (monofacial)
            productId: '7027757023441',
            variantId: '41611172741329',
        },
        {
            ...SP_160W_BIFACIAL, // (bifacial)
            productId: '7468127224017',
            variantId: '42234708820177',
        },
        {
            ...SP_220W, // (monofacial)
            productId: '7609705595089',
            variantId: '42613373141201',
        },
        {
            ...SP_220W_BIFACIAL, // (bifacial)
            productId: '7027754926289',
            variantId: '41630471913681',
        },
    ],
    products: [
        {
            ...RIVER_2_230,
            productId: '7504115826897',
            variantId: '42346262692049',
            link_more: 'https://za.ecoflow.com/products/river-2-230-portable-power-station?variant=42346262692049',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Laptop', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w'],
        },
        {
            ...RIVER_2,
            productId: '7187058000081',
            variantId: '41462251815121',
            link_more: 'https://za.ecoflow.com/products/river-2-portable-power-station?variant=41462251815121',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Laptop', 'MiniFridge'],
            childrenIds: ['45w', '60w', '110w'],
        },
        {
            ...RIVER_2_MAX,
            productId: '7187064389841',
            variantId: '41935487729873',
            link_more: 'https://za.ecoflow.com/products/river-2-max-portable-power-station?variant=41462253813969',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Laptop', 'MiniFridge'],
            childrenIds: ['60w', '110w', '160w', '160w_bifacial'],
        },
        {
            ...RIVER_2_PRO,
            productId: '7187064389841',
            variantId: '41935487729873',
            link_more: 'https://za.ecoflow.com/products/river-2-pro-portable-power-station?variant=41935487729873',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'Laptop', 'MiniFridge'],
            childrenIds: ['110w', '160w', '220w', '220w_bifacial'],
        },
        {
            ...RIVER_PRO,
            productId: '7027757121745',
            variantId: '41891136274641',
            link_more: 'https://za.ecoflow.com/products/river-pro-portable-power-station?variant=41891136274641',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['60w', '110w', '160w', '160w_bifacial'],
        },
    ],
};

const dataKR: DataLocalType = {
    solar_panels: [
        {
            ...SP_60W,
            productId: '7787975639209',
            variantId: '43240642937001',
        },
        {
            ...SP_110W,
            productId: '7429921112233',
            variantId: '44206195343529',
        },
        {
            ...SP_160W,
            productId: '7429922750633',
            variantId: '43800027037865',
        },
        {
            ...SP_220W,
            productId: '7759732441257',
            variantId: '43800028414121',
        },
        {
            ...SP_400W,
            productId: '7429924946089',
            variantId: '46400220397737',
        },
    ],
    products: [
        {
            ...DELTA_2,
            productId: '8687916187817',
            variantId: '46400158990505',
            link_more: 'https://kr.ecoflow.com/products/ecoflow-delta2',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Drone', 'Laptop'],
            childrenIds: ['110w', '160w', '220w', '400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '8687917072553',
            variantId: '46400200343721',
            link_more: 'https://kr.ecoflow.com/products/delta2-max',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Drone', 'Laptop'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...RIVER_3,
            productId: '8499061194921',
            variantId: '46312076738729',
            link_more: 'https://kr.ecoflow.com/products/river-3-ups-portable-power-station',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Camera', 'Fan', 'CarFridge', 'Projector'],
            childrenIds: ['60w', '110w'],
        },
    ],
};

const dataAll: { [key: string]: DataLocalType } = {
    us: dataUS,
    ca: dataCA,
    eu: dataEU,
    uk: dataUK,
    de: dataDE,
    fr: dataFR,
    it: dataIT,
    es: dataES,
    nl: dataNL,
    au: dataAU,
    za: dataZA,
    kr: dataKR,
};

/**
 *
 * @param locale 国家locale
 */
export const getLocalData = (locale: string) => dataAll[locale] || null;

/**
 *  有效光照时常平均为每日6小时, 但是欧洲六站为5h, 日本4h
 * @param power 太阳能板功率
 * @param n 太阳能板子数量
 * @returns 生成的能量
 */
export const calcEnergyGeneration = (power: number, n: number, locale: string) =>
    Math.round(((power * 100) / 1000) * (['eu', 'uk', 'de', 'fr', 'it', , 'es', 'nl'].includes(locale) ? 5 : ['jp'].includes(locale) ? 4 : 6) * n) / 100;

// API接口
export const getAsynItem = async (item: SolarItemType | ProductItemType, locale: string): Promise<SolarItemType | ProductItemType> => {
    const { productId, variantId } = item;
    if (!productId || !variantId) return item;
    return await new Promise(async (resolve, reject) => {
        try {
            const res = await getSolarRecommand({ productId, variantId }, locale);
            if (res?.code === '0') {
                if (Object.keys(res?.data)?.length === 0) {
                    res.data = {
                        inventoryPolicy: 'deny',
                        inventoryQuantity: 0,
                    };
                }
                if (res?.data?.handle) {
                    item.link_more = `https://${locale}.ecoflow.com/products/${res?.data?.handle}?variant=${variantId}`;
                }
                item.link_buy = `https://${locale}.ecoflow.com/cart/${variantId}:1?channel=buy_button`;
                return resolve({ ...item, ...res?.data });
            } else {
                console.error(res?.message);
                return reject({ ...item });
            }
        } catch (e) {
            console.error(e);
            return reject({ ...item });
        }
    });
};

export const BungleImgMap: {
    [key: string]: {
        [key: string]: {
            [key: string]: string;
        };
    };
} = {
    trail200: {
        campingLight: {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768212786/trail200_light.png',
        },
        trailDCBag: {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768212584/trail200_bag.png',
        },
    },
    trail300: {
        '45w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768213000/trail300_45w.png',
        },
        '60w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768213191/trail300_60w.png',
        },
    },
    river3: {
        '45w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1756353217983/r3_45w.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384364/r3_45w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758195144085/r3_45w_eu.png',
        },
        '60w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1756353218205/r3_60w.png',
        },
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1756353218390/r3_110w.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384243/r3_110w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758195144264/r3_110w_eu.png',
        },
    },
    river3Plus: {
        '45w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768211629/r3p_45w.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384365/r3p_45w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758195144449/r3p_45w_eu.png',
        },
        '60w': {
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384244/r3p_60w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758195144631/r3p_60w_eu.png',
        },
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768211855/r3p_110w.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384366/r3p_110w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597337235/r3p_110w_eu.png',
        },
        '160w': {
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384242/r3p_160w_uk.png',
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768212065/r3p_160w.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597337382/r3p_160w_eu.png',
        },
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768212287/r3p_220w.png',
        },
    },
    river3Max: {
        '45w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768210595/r3m_45w.png',
        },
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768210835/r3m_110w.png',
        },
        '160w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768211016/r3m_160w.png',
        },
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768211337/r3m_220w.png',
        },
    },
    river2Pro: {
        '45w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768209616/r2p_45w.png',
        },
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768209874/r2p_110w.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384243/r2p_110w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597881677/r2p_110w_eu.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612325238/r2p_110w_za.png',
        },
        '160w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768210106/r2p_160w.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758615014646/r2p_160w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612325379/r2p_160w_za.png',
        },
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768210401/r2p_220w.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384243/r2p_220w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597946147/r2p_220w_eu.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758615015423/r2p_220w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612326498/r2p_220w_za.png',
        },
        '220w_bifacial': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612325658/r2p_220w_bifacial_za.png',
        },
    },
    river2Pro700: {
        '160w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758614744219/r2p_700_160w_au.png',
        },
        '220w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758614744393/r2p_700_220w_bifacial_au.png',
        },
    },
    river2Max: {
        '60w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612323418/r2m_60w_za.png',
        },
        '110w': {
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384244/r2m_110w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758195143694/r2m_110w_eu.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758614743388/r2m_110w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612323637/r2m_110w_za.png',
        },
        '160w': {
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384239/r2m_160w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758850908192/r2m_160w_eu.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758614743429/r2m_160w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612324499/r2m_160w_za.png',
        },
        '160w_bifacial': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612323900/r2m_160w_bifacial_za.png',
        },
    },
    river2: {
        '45w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612321902/r2_45w_za.png',
        },
        '60w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758614742352/r2_60w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612506295/r2_60w_za.png',
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599301690/r2_60w_kr.png',
        },
        '110w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758614742661/r2_110w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612322139/r2_110w_za.png',
        },
    },
    river2230: {
        '45w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612322339/r2_230_45w_za.png',
        },
        '60w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612322738/r2_230_60w_za.png',
        },
        '110w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612323340/r2_230_110w_za.png',
        },
    },
    riverPro: {
        '60w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612326811/rp_60w_za.png',
        },
        '110w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612772006/rp_110w_za.png',
        },
        '160w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612772924/rp_160w_za.png',
        },
        '160w_bifacial': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758612772046/rp_160w_bifacial_za.png',
        },
    },
    delta2: {
        '110w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599111437/d2_110w_kr.png',
        },
        '160w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599208855/d2_160w_kr.png',
        },
        '220w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599111437/d2_220w_kr.png',
        },
        '400w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599111453/d2_400w_kr.png',
        },
    },
    delta2Max: {
        '160w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599111453/d2m_160w_kr.png',
        },
        '220w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599111485/d2m_220w_kr.png',
        },
        '400w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599111461/d2m_400w_kr.png',
        },
    },
};

export const formatNumber = (num: number, locale: string) => {
    if (typeof num !== 'number' || !num) return num;
    // 千分位分隔符为英文句号的站点
    const THOUSANDS_SEPARATOR_PERIOD_SITES: string[] = ['eu', 'it', 'es', 'nl'];
    // 千分位分隔符为空格的站点
    const THOUSANDS_SEPARATOR_SPACE_SITES: string[] = ['de', 'fr'];
    // 小数点分割符为逗号的站点
    const DECIMAL_SEPARATOR_COMMA_SITES: string[] = ['de', 'fr', 'it', 'es', 'nl'];
    const _getThousandsSeparator = (locale: string) => {
        if (THOUSANDS_SEPARATOR_PERIOD_SITES.includes(locale)) {
            return '.';
        }
        if (THOUSANDS_SEPARATOR_SPACE_SITES.includes(locale)) {
            return '\u202F';
        }
        return ',';
    };

    const _getDecimalSeparator = (locale: string) => {
        if (DECIMAL_SEPARATOR_COMMA_SITES.includes(locale)) {
            return ',';
        }
        return '.';
    };

    let strValue: string = num.toString();
    const thousandsSeparator = _getThousandsSeparator(locale);
    const decimalSeparator = _getDecimalSeparator(locale);

    if (DECIMAL_SEPARATOR_COMMA_SITES.includes(locale)) {
        strValue = strValue.replace('.', decimalSeparator);
    }
    const reg = /(\d)(?=(\d{3})+$)/g;
    strValue = strValue.replace(reg, `$1${thousandsSeparator}`);
    return strValue;
};
